import { decodeWithKeyAndShift, encodeWithKeyAndShift } from './base64Utils';
import endpoints from '../config/endpoints';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export class ApiInputValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ApiInputValidationError';
    }
}

export const isValidUuid = (value: unknown): value is string =>
    typeof value === 'string' && UUID_PATTERN.test(value);

export const requireUuid = (value: unknown, field: string): string => {
    if (!isValidUuid(value)) {
        throw new ApiInputValidationError(`${field} must be a valid UUID.`);
    }
    return value;
};

// Match only the requested API resources, not other beneficiary lookup routes.
export const validateGetApiInputs = (endpoint: string): void => {
    const pathname = new URL(endpoint, 'http://localhost').pathname;
    const path = pathname.replace(/^.*\/v1\//, '/').replace(/\/$/, '');
    const rules: Array<[RegExp, string, boolean?]> = [
        [/^\/child\/parent-illness(?:\/([^/]+))?$/, 'childBenefId'],
        [/^\/(?:benef\/)?child\/services\/completed(?:\/([^/]+))?$/, 'benfId'],
        [/^\/children\/child(?:\/([^/]+))?$/, 'childBenefId'],
        [/^\/(?:benef\/)?child\/services(?:\/((?!hbnc$|hbyc$|subcategories$)[^/]+))?$/, 'childBeneficiaryId'],
        [/^\/(?:benef\/)?child\/services\/subcategories\/hbyc(?:\/([^/]+))?(?:\/([^/]+))?$/, 'childBenefId', true],
        [/^\/self-registration\/states\/([^/]*)\/district$/, 'stateId'],
        [/^\/states\/no-hierarchy\/([^/]*)\/districts$/, 'stateId'],
        [/^\/work-plans\/beneficiaries\/download(?:\/([^/]+))?$/, 'workplanId'],
    ];
    // /beneficiaries itself is also a valid collection endpoint.
    const beneficiary = path.match(/^\/beneficiaries\/([^/]+)$/);
    const isStaticEndpoint = Object.values(endpoints).some(value => typeof value === 'string' &&
        new URL(value, 'http://localhost').pathname === pathname);
    if (beneficiary && !isStaticEndpoint) {
        requireUuid(beneficiary[1], 'benfId');
    }
    for (const [pattern, field, hasHbycNo] of rules) {
        const match = path.match(pattern);
        if (!match) continue;
        requireUuid(match[1], field);
        if (hasHbycNo && (!/^\d+$/.test(match[2] || '') || !Number.isSafeInteger(Number(match[2])))) {
            throw new ApiInputValidationError('hbycNo must be a numeric integer.');
        }
        return;
    }
};

export const normalizeChildUpdatePayload = (payload: any): any => {
    const weight = payload?.weightAtBirth;
    if (weight === undefined || weight === null) return payload;
    if (typeof weight === 'string' && weight.trim() === '') {
        return { ...payload, weightAtBirth: null };
    }
    if ((typeof weight !== 'number' && typeof weight !== 'string') ||
        (typeof weight === 'string' && !/^\d+$/.test(weight.trim())) ||
        !Number.isSafeInteger(Number(weight))) {
        throw new ApiInputValidationError('weightAtBirth must be an integer when provided.');
    }
    return typeof weight === 'number' ? payload : { ...payload, weightAtBirth: Number(weight) };
};

export const normalizeWorkplanDownloadPayload = (payload: any): any => {
    const hasWorkplanId = payload && ('workplanId' in payload || 'workPlanId' in payload);
    // Existing individual/date-based downloads do not select workplan-download mode.
    if (!hasWorkplanId && payload?.sessionDueDate && payload?.serviceCategoryId &&
        payload?.mode !== 'workplan' && payload?.isWorkplanDownload !== true) return payload;
    const workplanId = requireUuid(payload?.workplanId ?? payload?.workPlanId, 'workplanId');
    return { ...payload, workplanId };
};

const validateChildRegistrationPayload = (payload: any): any => {
    requireUuid(payload?.benefId, 'benefId');
    return payload;
};

/** Validate decoded inputs and preserve the existing encrypted/plain envelope contract. */
export const prepareApiRequestData = (method: string, endpoint: string, data: any): any => {
    const path = new URL(endpoint, 'http://localhost').pathname.replace(/^.*\/v1\//, '/').replace(/\/$/, '');
    const requestMethod = method.toLowerCase();
    let preparePayload: ((payload: any) => any) | undefined;

    if (requestMethod === 'put' && path === '/children') {
        preparePayload = normalizeChildUpdatePayload;
    } else if (requestMethod === 'post' && /^\/beneficiaries\/download(?:Mob)?$/.test(path)) {
        preparePayload = normalizeWorkplanDownloadPayload;
    } else if (requestMethod === 'post' && path === '/beneficiaries/check-child-reg-inside-rch') {
        preparePayload = validateChildRegistrationPayload;
    }

    if (!preparePayload) return data;
    const wrapped = data && Object.prototype.hasOwnProperty.call(data, 'data');
    const payload = wrapped ? JSON.parse(decodeWithKeyAndShift(data.data)) : data;
    const normalized = preparePayload(payload);
    if (normalized === payload) return data;
    return wrapped ? { ...data, data: encodeWithKeyAndShift(JSON.stringify(normalized)) } : normalized;
};